import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api';

interface Resource<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Fetches a resource and re-fetches when its dependencies change.
 *
 * <p>Late responses from a superseded request are discarded, so typing in a filter box cannot leave
 * the table showing results for a query the user has already moved on from.
 */
export function useResource<T>(load: () => Promise<T>, dependencies: unknown[]): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const generation = useRef(0);

  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    const current = ++generation.current;
    setLoading(true);
    setError(null);

    loadRef
      .current()
      .then((result) => {
        if (generation.current !== current) return;
        setData(result);
      })
      .catch((caught: unknown) => {
        if (generation.current !== current) return;
        setError(caught instanceof ApiError ? caught.message : 'Something went wrong loading this page');
      })
      .finally(() => {
        if (generation.current === current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { data, error, loading, reload };
}

/** Debounces a value, so a search box does not fire a request per keystroke. */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}
